import React, { useState, useEffect, useMemo, useRef } from 'react'
import TemplatesLibrary from './TemplatesLibrary'
import TemplateForm from './TemplateForm'
import {
  BellIcon,
  PlusIcon,
  CalendarIcon,
  RefreshIcon,
  DocumentTextIcon,
  SearchIcon,
  DuplicateIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationCircleIcon,
  ClockIcon,
  ArrowRightIcon,
  ChevronRightIcon,
  PlayIcon,
  UsersIcon,
  XIcon,
  ArrowLeftIcon,
  AdjustmentsIcon,
  GlobeAltIcon,
  CheckIcon,
  ExclamationIcon,
  ChevronDownIcon,
  LocationMarkerIcon,
  TrashIcon
} from '@heroicons/react/outline'
import Loader from '../UI/Loader'
import connexion from '../../connexion'

// --- Constantes ---


const DEFAULT_LANGUAGES = {
  fr: { title: '', body: '', filled: false },
  en: { title: '', body: '', filled: false },
  de: { title: '', body: '', filled: false },
  nl: { title: '', body: '', filled: false },
  it: { title: '', body: '', filled: false },
  es: { title: '', body: '', filled: false },
  pt: { title: '', body: '', filled: false }
}

const LANG_LABELS = {
  fr: { label: 'Français', flag: '🇫🇷', native: 'Obligatoire' },
  en: { label: 'English', flag: '🇬🇧', native: 'Optionnel' },
  de: { label: 'Deutsch', flag: '🇩🇪', native: 'Optionnel' },
  nl: { label: 'Nederlands', flag: '🇳🇱', native: 'Optionnel' },
  it: { label: 'Italiano', flag: '🇮🇹', native: 'Optionnel' },
  es: { label: 'Español', flag: '🇪🇸', native: 'Optionnel' },
  pt: { label: 'Português', flag: '🇵🇹', native: 'Optionnel' }
}

// --- Helpers ---

function getTitle(translations, lang_default) {
  if (!translations || !translations.length)
    return '(Sans titre)'
  for (let i = 0; i < translations.length; i++) {
    if (translations[i].lang === lang_default && translations[i].title)
      return translations[i].title
  }
  return translations[0].title || '(Sans titre)'
}

function getBody(translations, lang_default) {
  if (!translations || !translations.length)
    return ''
  for (let i = 0; i < translations.length; i++) {
    if (translations[i].lang === lang_default && translations[i].content)
      return translations[i].content
  }
  return translations[0].content || ''
}

function getDestinationLabel(redirect) {
  if (!redirect)
    return 'Accueil'
  switch (redirect.type) {
    case 'home': return 'Accueil'
    case 'animations': return 'Planning des animations'
    case 'membres': return 'Mon compte'
    case 'page': return 'Page personnalisée'
    case 'category': return 'Rubrique'
    default: return redirect.type || 'Accueil'
  }
}

function formatDate(ts) {
  if (!ts || ts === false)
    return '—'
  const d = new Date(ts * 1000)
  return d.toLocaleDateString('fr-FR') + ' à ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
}

function getAudienceLabel(user_list) {
  if (!user_list || user_list.length === 0)
    return 'Tous les campeurs'
  return user_list.length + ' campeur' + (user_list.length > 1 ? 's' : '') + ' sélectionné' + (user_list.length > 1 ? 's' : '')
}

function getCronStatus(cn, now) {
  if (cn.sent !== false && cn.sent !== null && cn.sent !== undefined)
    return 'sent'
  if (cn.time > now)
    return 'scheduled'
  return 'failed'
}

function getValidationForLang(translations, lang) {
  const data = translations[lang]
  const isFr = lang === 'fr'
  if (isFr && !data.title.trim() && data.body.trim())
    return { valid: false, error: 'Le titre français est obligatoire.' }
  if (isFr && !data.body.trim() && data.title.trim())
    return { valid: false, error: 'Le message français est obligatoire.' }
  if (isFr && !data.title.trim() && !data.body.trim())
    return { valid: false, error: 'Le français est la langue de diffusion obligatoire.' }
  if (data.title.length > 40)
    return { valid: false, error: 'Titre trop long (' + data.title.length + '/40 caract.)' }
  if (data.body.length > 140)
    return { valid: false, error: 'Corps trop long (' + data.body.length + '/140 caract.)' }
  return { valid: true }
}

// --- Panneau de détail (tiroir latéral) ---

const DRAWER_LANGS = ['fr', 'en', 'de', 'nl', 'it', 'es', 'pt']

function buildLanguagesFromTranslations(translations) {
  const languages = {}
  for (let i = 0; i < DRAWER_LANGS.length; i++)
    languages[DRAWER_LANGS[i]] = { title: '', body: '', filled: false }
  if (!translations)
    return languages
  for (let i = 0; i < translations.length; i++) {
    const t = translations[i]
    if (languages[t.lang] !== undefined)
      languages[t.lang] = { title: t.title || '', body: t.content || '', filled: !!(t.title || t.content) }
  }
  return languages
}

function NotificationDetailDrawer({ cn, now, onClose, onDuplicate, onCancelScheduled, onDelete, hideActions }) {
  const [activeLang, setActiveLang] = useState('fr')
  const [isClosing, setIsClosing] = useState(false)

  function handleClose() {
    setIsClosing(true)
    setTimeout(onClose, 320)
  }

  const status = getCronStatus(cn, now)
  const languages = buildLanguagesFromTranslations(cn.translations)
  const audienceLabel = getAudienceLabel(cn.user_list)
  const audienceCount = cn.user_list ? cn.user_list.length : 0
  const destination = getDestinationLabel(cn.redirect)
  const dateLabel = formatDate(cn.time)
  const shortId = cn._id ? String(cn._id).slice(-8).toUpperCase() : '—'

  function renderStatusBanner() {
    if (status === 'scheduled')
    {
      return (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl p-3 flex items-start gap-2.5 animate-fade-in">
          <ClockIcon className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <span className="font-bold block">Action planifiée dans le calendrier</span>
            <p className="mt-0.5">Ce push sera automatiquement validé et envoyé le <strong>{dateLabel}</strong>.</p>
          </div>
        </div>
      )
    }
    if (status === 'sent')
    {
      return (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl p-3 flex items-start gap-2.5 animate-fade-in">
          <CheckIcon className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <span className="font-bold block">Transmission réussie</span>
          </div>
        </div>
      )
    }
    if (status === 'failed')
    {
      return (
        <div className="bg-rose-50 border border-rose-200 text-rose-900 rounded-xl p-3 flex items-start gap-2.5 animate-fade-in">
          <ExclamationIcon className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <span className="font-bold block">Erreur de transmission (Incident d'envoi)</span>
            <p className="mt-0.5 leading-normal">Cette notification n'a pas pu être envoyée à la date prévue.</p>
          </div>
        </div>
      )
    }
    return null
  }

  function renderStatusBadge() {
    if (status === 'scheduled')
      return <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-500 border border-amber-600 text-white text-sm font-bold rounded-lg">● Programmée</span>
    if (status === 'sent')
      return <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-600 border border-emerald-700 text-white text-sm font-bold rounded-lg">● Envoyée</span>
    if (status === 'failed')
      return <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-rose-600 border border-rose-700 text-white text-sm font-bold rounded-lg">● Manquée</span>
    return <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-slate-100 text-slate-700 text-sm font-bold rounded-lg border border-slate-200">● Brouillon</span>
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div
        className={'absolute inset-0 bg-slate-900 bg-opacity-40 ' + (isClosing ? 'animate-fade-out' : 'animate-fade-in')}
        style={isClosing ? { animationFillMode: 'forwards' } : {}}
        onClick={handleClose}
      />
      <div
        className={'relative h-full w-full max-w-xl bg-white shadow-2xl border-l border-slate-200 flex flex-col overflow-hidden text-sm z-10 ' + (isClosing ? 'animate-slide-out-right' : 'animate-slide-in-right')}
        style={isClosing ? { animationFillMode: 'forwards' } : {}}
      >

        {/* Contenu défilant */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">

          {/* En-tête */}
          <div className="flex justify-between items-start">
            <div className="space-y-1">
              <span className="text-sm font-mono font-bold text-slate-400 uppercase tracking-widest block">IDENTIFIANT DE NOTIFICATION : #{shortId}</span>
              <div className="flex items-center gap-2 mt-0.5">
                <h2 className="text-xl font-bold text-slate-900 tracking-tight my-0">Détails de l'envoi</h2>
                {renderStatusBadge()}
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-1.5 hover:bg-slate-100 text-slate-400 hover:text-slate-700 rounded-full transition-colors border border-slate-200 cursor-pointer select-none"
              style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
            >
              <XIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Bandeau de statut */}
          {renderStatusBanner()}

          {/* Onglets langues */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-bold text-slate-600 flex items-center gap-1" style={{ margin: 0 }}>
                <GlobeAltIcon className="w-3.5 h-3.5 text-slate-400" /> Langues configurées
              </label>
              <span className="text-sm text-slate-400 font-mono">Cliquez pour inspecter la version traduite</span>
            </div>
            <div className="flex flex-wrap gap-1 bg-slate-50 p-1 rounded-xl border border-slate-200">
              {DRAWER_LANGS.map(lang => {
                const hasText = languages[lang].filled
                const isActive = activeLang === lang
                return (
                  <button
                    key={lang}
                    onClick={() => { if (hasText) setActiveLang(lang) }}
                    disabled={!hasText}
                    className={
                      'py-1.5 px-3 rounded-lg text-sm font-semibold flex items-center gap-1 transition-all ' +
                      (isActive
                        ? 'bg-white text-slate-900 shadow-sm border border-slate-200'
                        : hasText
                          ? 'text-slate-600 hover:text-slate-900 hover:bg-slate-100 cursor-pointer'
                          : 'text-slate-300 cursor-not-allowed opacity-50')
                    }
                    style={{ backgroundImage: 'none', backgroundColor: isActive ? 'white' : 'transparent' }}
                  >
                    <span className="uppercase">{lang}</span>
                    <span className={'w-1 h-1 rounded-full ' + (hasText ? 'bg-emerald-500' : 'bg-transparent')}></span>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Contenu titre + message */}
          <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 space-y-3">
            <div className="space-y-1">
              <span className="text-sm font-mono font-bold text-slate-400 uppercase">Titre [{activeLang}] :</span>
              <span className="font-bold text-slate-900 text-sm block tracking-wide mt-0.5">
                {languages[activeLang].title || '—'}
              </span>
            </div>
            <div className="space-y-1 border-t border-slate-200 pt-3">
              <span className="text-sm font-mono font-bold text-slate-400 uppercase">Message [{activeLang}] :</span>
              <p className="text-slate-700 text-sm leading-relaxed mt-0.5 whitespace-pre-wrap">
                {languages[activeLang].body || '—'}
              </p>
            </div>
          </div>

          {/* Audience + destination */}
          <div className="grid grid-cols-2 gap-4 text-sm font-mono">
            <div className="p-3 bg-white border border-slate-200 rounded-xl shadow-sm">
              <span className="text-sm text-slate-400 font-bold block mb-1">AUDIENCE CIBLE</span>
              <div className="flex items-center gap-1.5 text-slate-800">
                <UsersIcon className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <span className="font-bold text-sm truncate">{audienceLabel}</span>
              </div>
              {audienceCount > 0 && (
                <span className="text-sm text-indigo-700 block mt-1 font-bold">({audienceCount} campeur{audienceCount > 1 ? 's' : ''} récepteur{audienceCount > 1 ? 's' : ''})</span>
              )}
            </div>
            <div className="p-3 bg-white border border-slate-200 rounded-xl shadow-sm">
              <span className="text-sm text-slate-400 font-bold block mb-1">DEEP-LINKING</span>
              <div className="flex items-center gap-1.5 text-slate-800">
                <LocationMarkerIcon className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <span className="font-bold text-sm truncate">{destination}</span>
              </div>
              <span className="text-sm text-slate-400 block mt-1">Redirection au clic de premier niveau</span>
            </div>
          </div>

        </div>

        {/* Pied de page épinglé */}
        <div className="p-5 bg-slate-50 border-t border-slate-200 flex flex-wrap items-center justify-between gap-3 text-sm font-semibold shadow-sm">
          <div className="flex flex-col">
            <span className="text-sm uppercase font-bold text-slate-400 font-mono tracking-wider">
              {status === 'scheduled' ? 'Envoi programmé pour' : status === 'failed' ? 'Prévu le' : 'Transmis le'}
            </span>
            <span className="text-slate-700 font-mono font-bold text-sm mt-0.5">{dateLabel}</span>
          </div>
          <div className="flex items-center gap-2">
            {!hideActions && (
              <button
                onClick={() => { onDuplicate(cn); handleClose() }}
                className="px-4 py-2 border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 rounded-xl transition-all flex items-center gap-1.5 cursor-pointer font-bold text-sm"
                style={{ backgroundImage: 'none' }}
              >
                <DuplicateIcon className="w-4 h-4 text-slate-400" /> Dupliquer & Ré-émettre
              </button>
            )}
            {status === 'scheduled' && (
              <button
                onClick={() => { onCancelScheduled(cn); handleClose() }}
                className="px-4 py-2 bg-rose-50 hover:bg-rose-100 border border-rose-200 text-rose-700 rounded-xl transition-all cursor-pointer font-bold text-sm"
                style={{ backgroundImage: 'none', backgroundColor: '#fff1f2' }}
              >
                Annuler l'envoi
              </button>
            )}
            {!hideActions && status === 'failed' && (
              <button
                onClick={() => { onDelete(cn._id); handleClose() }}
                className="px-4 py-2 bg-slate-100 hover:bg-rose-50 text-slate-600 hover:text-rose-600 rounded-xl transition-all flex items-center gap-1 border border-slate-200 cursor-pointer font-bold text-sm"
                style={{ backgroundImage: 'none', backgroundColor: '#f1f5f9' }}
              >
                <TrashIcon className="w-4 h-4" /> Supprimer
              </button>
            )}
          </div>
        </div>

      </div>
    </div>
  )
}

// --- Formulaire de création ---

function NotificationFormView({ initialNotification, defaultSendType, clientSegmentInbound, lang_default, onCancel, onSubmit, onOpenLibrary }) {
  const [activeLang, setActiveLang] = useState('fr')
  const [translations, setTranslations] = useState(JSON.parse(JSON.stringify(DEFAULT_LANGUAGES)))
  const [destination, setDestination] = useState('{"type":"home"}')
  const [pages, setPages] = useState([])
  const [categories, setCategories] = useState([])
  const [groups, setGroups] = useState([])
  const [audienceOption, setAudienceOption] = useState('all')
  const [overlapStart, setOverlapStart] = useState('')
  const [overlapEnd, setOverlapEnd] = useState('')
  const [audienceUserIds, setAudienceUserIds] = useState(null)
  const [audienceLoading, setAudienceLoading] = useState(false)
  const [audienceCountUsers, setAudienceCountUsers] = useState(null)
  const [audienceCountPush, setAudienceCountPush] = useState(null)
  const [allCounts, setAllCounts] = useState({})
  const [sendType, setSendType] = useState(defaultSendType || 'now')
  const [accommodationFilter, setAccommodationFilter] = useState('all')
  const [substep, setSubstep] = useState('edit')
  const [isSending, setIsSending] = useState(false)

  useEffect(() => {
    const statuses = ['all', 'on_site', 'arriving_today', 'arriving_7_days', 'leaving_7_days']
    Promise.all(
      statuses.map(status => {
        const body = { status }
        if (accommodationFilter !== 'all')
          body.accommodation_filter = accommodationFilter
        return fetch(connexion.connect.url + 'webapp/notifications/audience', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        })
        .then(r => r.json())
        .then(data => ({ status, users: (data && data !== false) ? data.count_users : null, push: (data && data !== false) ? data.count_push : null }))
        .catch(() => ({ status, users: null, push: null }))
      })
    )
    .then(results => {
      const counts = {}
      for (let i = 0; i < results.length; i++)
        counts[results[i].status] = { users: results[i].users, push: results[i].push }
      setAllCounts(counts)
    })
    .catch(() => {})
  }, [accommodationFilter])

  useEffect(() => {
    Promise.all([
      fetch(connexion.connect.url + 'webapp/pages/list-no-content').then(r => r.json()),
      fetch(connexion.connect.url + 'webapp/categories/list').then(r => r.json()),
      fetch(connexion.connect.url + 'webapp/groups/list').then(r => r.json()),
    ])
    .then(([pagesData, categoriesData, groupsData]) => {
      if (pagesData && pagesData !== false) setPages(pagesData)
      if (categoriesData && categoriesData !== false) setCategories(categoriesData)
      if (groupsData && groupsData !== false) setGroups(groupsData)
    })
    .catch(() => {})
  }, [])

  const nowObj = new Date()
  const pad = (n) => String(n).padStart(2, '0')
  const plusOneHour = new Date(nowObj.getTime() + 60 * 60 * 1000)
  const defaultScheduledStr = plusOneHour.getFullYear() + '-' + pad(plusOneHour.getMonth() + 1) + '-' + pad(plusOneHour.getDate()) + 'T' + pad(plusOneHour.getHours()) + ':' + pad(plusOneHour.getMinutes())
  const [scheduledDate, setScheduledDate] = useState(defaultScheduledStr)

  useEffect(() => {
    if (initialNotification) {
      const clonedLangs = JSON.parse(JSON.stringify(initialNotification.languages || DEFAULT_LANGUAGES))
      setTranslations(clonedLangs)
      setDestination(initialNotification.redirect || '{"type":"home"}')
      setAudienceOption(initialNotification.audienceOption || 'all')
      setOverlapStart(initialNotification.overlapStart || '')
      setOverlapEnd(initialNotification.overlapEnd || '')
      setSendType(initialNotification.sendType || 'now')
      if (initialNotification.scheduledDate)
        setScheduledDate(initialNotification.scheduledDate)
    }
    else {
      setTranslations(JSON.parse(JSON.stringify(DEFAULT_LANGUAGES)))
    }
    setSubstep('edit')
  }, [initialNotification])

  useEffect(() => {
    setAudienceLoading(true)
    const timer = setTimeout(() => {
      const body = { status: audienceOption }
      if (audienceOption === 'overlap_period') {
        body.date_from = overlapStart ? Math.round(new Date(overlapStart).getTime() / 1000) : null
        body.date_to = overlapEnd ? Math.round(new Date(overlapEnd).getTime() / 1000) : null
      }
      if (accommodationFilter !== 'all')
        body.accommodation_filter = accommodationFilter
      fetch(connexion.connect.url + 'webapp/notifications/audience', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      })
      .then(r => r.json())
      .then(data => {
        if (data && data !== false) {
          setAudienceCountUsers(data.count_users)
          setAudienceCountPush(data.count_push)
          setAudienceUserIds(data.user_ids)
        }
        setAudienceLoading(false)
      })
      .catch(() => setAudienceLoading(false))
    }, 600)
    return () => clearTimeout(timer)
  }, [audienceOption, overlapStart, overlapEnd, accommodationFilter])

  function handleTextChange(field, value) {
    setTranslations(prev => {
      const current = { ...prev[activeLang] }
      current[field] = value
      current.filled = current.title.trim().length > 0 || current.body.trim().length > 0
      return { ...prev, [activeLang]: current }
    })
  }

  function isDateInvalid() {
    if (sendType !== 'scheduled')
      return false
    return new Date(scheduledDate).getTime() < Date.now()
  }

  function isFormSubmittable() {
    if (!translations.fr.title.trim() || !translations.fr.body.trim())
      return false
    const keys = Object.keys(translations)
    for (let i = 0; i < keys.length; i++) {
      const k = keys[i]
      if (translations[k].filled && !getValidationForLang(translations, k).valid)
        return false
    }
    if (sendType === 'scheduled' && isDateInvalid())
      return false
    return true
  }

  function getLangIndicatorClass(lang) {
    const data = translations[lang]
    if (!data.filled)
      return lang === 'fr' ? 'bg-rose-400' : 'bg-gray-300'
    if (!getValidationForLang(translations, lang).valid)
      return 'bg-rose-500'
    return 'bg-emerald-500'
  }

  function handlePreSubmit() {
    setSubstep('summary')
  }

  function handleFinalEmit() {
    onSubmit({
      title: translations.fr.title,
      body: translations.fr.body,
      languages: translations,
      redirect: destination,
      audienceOption,
      accommodationFilter,
      audienceCountUsers,
      audienceUserIds,
      sendType,
      scheduledDate: sendType === 'scheduled' ? scheduledDate : undefined
    })
  }

  const destinations = useMemo(() => {
    const lang = lang_default || 'fr'
    const list = [
      { value: '{"type":"home"}', label: "Page d'accueil" },
      { value: '{"type":"animations"}', label: "Planning d'animations" },
      { value: '{"type":"membres"}', label: 'Page membres' },
    ]
    for (let i = 0; i < categories.length; i++) {
      const cat = categories[i]
      let name = ''
      for (let j = 0; j < cat.translations.length; j++) {
        if (cat.translations[j].lang === lang) { name = cat.translations[j].name; break }
      }
      if (!name && cat.translations.length > 0) name = cat.translations[0].name
      if (!name) continue
      list.push({ value: JSON.stringify({ type: 'category', category: String(cat._id) }), label: 'Rubrique — ' + name })
    }
    for (let i = 0; i < pages.length; i++) {
      const page = pages[i]
      if (page.category !== null) continue
      let name = ''
      for (let j = 0; j < page.translations.length; j++) {
        if (page.translations[j].lang === lang) { name = page.translations[j].name; break }
      }
      if (!name && page.translations.length > 0) name = page.translations[0].name
      if (!name) continue
      list.push({ value: JSON.stringify({ type: 'page', page: String(page._id) }), label: 'Page — ' + name })
    }
    return list
  }, [pages, categories, groups, lang_default])

  const activeLangValidation = getValidationForLang(translations, activeLang)

  const audienceLabel = useMemo(() => {
    const STATUS_LABELS = {
      custom_segment: 'Segment de recherche de contacts',
      all: 'Tous les campeurs inscrits',
      on_site: 'Sur site en ce moment',
      arriving_today: "Arrivent aujourd'hui",
      arriving_7_days: 'Arrivent dans les 7 jours',
      leaving_7_days: 'Partent dans les 7 jours',
      overlap_period: overlapStart && overlapEnd
        ? 'Séjour entre le ' + new Date(overlapStart).toLocaleDateString('fr-FR') + ' et le ' + new Date(overlapEnd).toLocaleDateString('fr-FR')
        : 'Séjour qui chevauche la période',
    }
    return STATUS_LABELS[audienceOption] || 'Tous les campeurs inscrits'
  }, [audienceOption, overlapStart, overlapEnd])
  const previewTitle = translations[activeLang].title.trim() || '[Titre en ' + LANG_LABELS[activeLang].label + ']'
  const previewBody = translations[activeLang].body.trim() || '[Le corps de votre notification apparaîtra ici au fur et à mesure de votre saisie...]'
  const submittable = isFormSubmittable()

  return (
    <div className="w-full text-slate-900 space-y-6">

      {/* En-tête */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between border-b border-gray-100 pb-4 gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={onCancel}
            className="p-2 hover:bg-slate-100 rounded-xl text-slate-500 hover:text-slate-800 transition-colors cursor-pointer"
            style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
          <div>
            <span className="text-sm font-mono font-semibold text-slate-400 block">Hub PocketCamp</span>
            <h1 className="text-xl font-bold text-slate-900 tracking-tight my-0">
              {initialNotification ? 'Modifier la notification' : 'Créer une nouvelle notification'}
            </h1>
          </div>
        </div>
        {!initialNotification && (
          <button
            onClick={onOpenLibrary}
            className="text-sm font-semibold bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 text-indigo-700 px-4 py-2 rounded-xl transition-all flex items-center gap-1 cursor-pointer"
            style={{ backgroundImage: 'none' }}
          >
            Partir d'un template modèle
          </button>
        )}
      </div>

      {substep === 'success' ? (
        <div className="max-w-xl mx-auto py-8 animate-fade-in">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xl p-8 text-center space-y-6">
            <div className="w-16 h-16 bg-emerald-50 border border-emerald-200 rounded-full flex items-center justify-center mx-auto">
              <CheckCircleIcon className="w-10 h-10 text-emerald-500 flex-shrink-0" />
            </div>
            <div className="space-y-2">
              <h2 className="text-lg font-bold text-slate-800 my-0">
                {sendType === 'scheduled' ? 'La notification est programmée !' : 'Message envoyé avec succès !'}
              </h2>
              <p className="text-sm text-slate-500 max-w-md mx-auto leading-relaxed my-0">
                {sendType === 'scheduled' ? (
                  <>Votre message push a bien été enregistré. Il sera émis de manière autonome le <strong className="text-slate-800 font-semibold">{scheduledDate.replace('T', ' ')}</strong>.</>
                ) : (
                  <>La notification push a été transmise en direct. Vos campeurs recevront l'alerte d'ici quelques instants.</>
                )}
              </p>
            </div>
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 text-left space-y-2 text-sm">
              <div className="flex justify-between border-b border-slate-200 pb-1.5">
                <span className="font-semibold text-slate-400 text-xs uppercase font-mono">Planification :</span>
                <span className="font-bold text-slate-800">
                  {sendType === 'scheduled' ? 'Programmé pour le ' + scheduledDate.replace('T', ' ') : '⚡ Envoi immédiat'}
                </span>
              </div>
              <div className="flex justify-between border-b border-slate-200 pb-1.5">
                <span className="font-semibold text-slate-400 text-xs uppercase font-mono">Audience :</span>
                <span className="font-bold text-slate-800">
                  {audienceLabel}{audienceCountUsers !== null ? ' (' + audienceCountUsers + ' campeur' + (audienceCountUsers > 1 ? 's' : '') + ' · ' + audienceCountPush + ' push)' : ''}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="font-semibold text-slate-400 text-xs uppercase font-mono">Langues envoyées :</span>
                <span className="font-bold text-indigo-600">
                  {Object.keys(translations).filter(k => translations[k].filled || k === 'fr').map(k => k.toUpperCase()).join(', ')}
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={onCancel}
              className="w-full font-semibold text-sm px-5 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl transition-all flex items-center justify-center gap-2 border border-indigo-700 shadow-sm cursor-pointer"
              style={{ backgroundImage: 'none', backgroundColor: '#4f46e5' }}
            >
              <CheckIcon className="w-4 h-4" />
              Terminer et retourner au Tableau de Bord
            </button>
          </div>
        </div>
      ) : (

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">

        {/* Colonne formulaire */}
        <div className="lg:col-span-3 space-y-6">

          {substep === 'summary' ? (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-6">
              <div className="border-b border-slate-100 pb-3 flex items-center justify-between">
                <h2 className="text-base font-bold text-slate-800 my-0">Résumé final de diffusion</h2>
                <span className="text-xs font-mono bg-indigo-50 border border-indigo-100 text-indigo-700 px-2.5 py-1 rounded-full font-bold">
                  Étape 2 sur 2
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-200 text-left">
                  <span className="text-xs uppercase font-bold tracking-wider text-slate-400 block mb-1">Cible destinataire</span>
                  <span className="font-bold text-slate-800 text-sm block">{audienceLabel}</span>
                  {audienceCountUsers !== null && (
                    <span className="text-xs font-mono font-bold text-indigo-700 mt-1 block">
                      <span role="img" aria-label="envoi immédiat">⚡</span> {audienceCountUsers} campeur{audienceCountUsers > 1 ? 's' : ''} · {audienceCountPush} push activé{audienceCountPush > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-200 text-left">
                  <span className="text-xs uppercase font-bold tracking-wider text-slate-400 block mb-1">Date d'émission</span>
                  <span className="font-bold text-slate-800 text-sm block">
                    {sendType === 'scheduled' ? '📅 Programmée' : '⚡ Envoi immédiat'}
                  </span>
                  {sendType === 'scheduled' && (
                    <span className="text-xs text-slate-500 font-mono mt-1 block">Le {scheduledDate.replace('T', ' à ')}</span>
                  )}
                </div>
              </div>
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-left space-y-3">
                <span className="text-xs uppercase font-extrabold text-slate-400 tracking-wider block">Payloads multilingues configurés</span>
                <div className="space-y-2 max-h-44 overflow-y-auto">
                  {Object.keys(translations).filter(k => translations[k].filled || k === 'fr').map(lang => (
                    <div key={lang} className="p-2.5 bg-white rounded-lg border border-slate-200 text-sm text-left relative">
                      <span className="absolute top-2 right-2 text-lg">{LANG_LABELS[lang].flag}</span>
                      <span className="font-mono text-xs font-bold text-slate-400 uppercase">{LANG_LABELS[lang].label}</span>
                      <div className="mt-1">
                        <span className="font-bold text-slate-800 text-sm block">{translations[lang].title || 'Titre de secours'}</span>
                        <p className="text-slate-500 mt-0.5 leading-relaxed text-xs my-0">{translations[lang].body || 'Pas de texte saisi...'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
             
              <div className="flex items-center gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setSubstep('edit')}
                  disabled={isSending}
                  className="px-4 py-2.5 rounded-lg border border-slate-200 text-slate-600 hover:text-slate-800 font-semibold text-sm transition-colors cursor-pointer"
                  style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
                >
                  Retour à l'édition
                </button>
                <button
                  type="button"
                  id="form-confirm-send-btn"
                  disabled={isSending}
                  onClick={() => {
                    if (isSending)
                      return ;
                    if (sendType === 'scheduled') {
                      handleFinalEmit()
                      return ;
                    }
                    setIsSending(true)
                    // Envoi réel déclenché ici (étape résumé), avant l'écran de succès
                    handleFinalEmit()
                    setTimeout(() => {
                      setIsSending(false)
                      setSubstep('success')
                    }, 1200)
                  }}
                  className={
                    'flex-1 font-semibold text-sm px-5 py-2.5 text-white rounded-lg transition-all text-center flex items-center justify-center gap-2 border shadow-sm ' +
                    (isSending
                      ? 'bg-indigo-400 border-indigo-400 cursor-not-allowed'
                      : 'bg-indigo-600 hover:bg-indigo-700 border-indigo-700 cursor-pointer')
                  }
                  style={{ backgroundImage: 'none', backgroundColor: isSending ? '#818cf8' : '#4f46e5' }}
                >
                  {isSending ? (
                    <>
                      <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>{sendType === 'scheduled' ? 'Programmation en cours...' : 'Envoi en cours...'}</span>
                    </>
                  ) : (
                    <>
                      <CheckIcon className="w-4 h-4" />
                      <span>{sendType === 'scheduled' ? 'Programmer la notification ⏰' : 'Confirmer et Envoyer maintenant ! 🚀'}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : (
            <>

          {clientSegmentInbound && (
            <div className="bg-emerald-50 text-emerald-900 border border-emerald-200 rounded-2xl p-4 flex gap-3 items-start">
              <div className="bg-emerald-500 text-white rounded-xl p-1 flex-shrink-0">
                <AdjustmentsIcon className="w-4 h-4" />
              </div>
              <div className="flex-1 text-sm">
                <span className="font-bold block text-sm">47 clients sélectionnés depuis votre recherche de contacts</span>
                <p className="text-emerald-700 mt-1 leading-relaxed">
                  Cette audience personnalisée a été automatiquement injectée. Votre message ne parviendra qu'à ce segment restreint.
                </p>
                <div className="mt-3">
                  <button
                    onClick={() => setAudienceOption('all')}
                    className="underline hover:no-underline font-bold text-emerald-800 hover:text-emerald-950 cursor-pointer text-sm"
                    style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
                  >
                    Élargir à tous les campeurs
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Section Message & Traductions */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-4">
            <div className="flex justify-between items-center border-b border-gray-100 pb-3">
              <div className="flex items-center gap-2">
                <GlobeAltIcon className="w-4 h-4 text-slate-500" />
                <h3 className="font-bold text-slate-800 text-sm my-0">Message & Traductions</h3>
              </div>
              <span className="text-sm text-slate-400 font-mono">Remplissez au moins le français</span>
            </div>

            {/* Onglets langues */}
            <div className="flex flex-wrap gap-1 bg-slate-50 p-1 rounded-xl border border-slate-200">
              {Object.keys(translations).map(lang => {
                const isSelected = activeLang === lang
                return (
                  <button
                    key={lang}
                    onClick={() => setActiveLang(lang)}
                    className={
                      'py-1 px-3 rounded-lg text-sm font-semibold flex items-center gap-1 transition-all ' +
                      (isSelected
                        ? 'bg-white text-slate-900 shadow-sm border border-slate-200'
                        : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100')
                    }
                    style={{ backgroundImage: 'none', backgroundColor: isSelected ? 'white' : 'transparent' }}
                  >
                    <span>{LANG_LABELS[lang].flag}</span>
                    <span className="uppercase">{lang}</span>
                    <span className={'w-1 h-1 rounded-full inline-block ' + getLangIndicatorClass(lang)}></span>
                  </button>
                )
              })}
            </div>

            <div className="bg-slate-50 rounded-xl p-3 flex justify-between items-center text-sm">
              <span className="text-slate-400 font-mono">
                Langue éditée : <strong className="text-slate-700">{LANG_LABELS[activeLang].label}</strong>
              </span>
              <span className={
                'text-sm uppercase px-1 rounded font-mono font-bold ' +
                (activeLang === 'fr' ? 'bg-rose-50 text-rose-700 border border-rose-100' : 'bg-slate-100 text-slate-500')
              }>
                {LANG_LABELS[activeLang].native}
              </span>
            </div>

            {/* Champ Titre */}
            <div className="space-y-1">
              <div className="flex justify-between items-baseline">
                <label className="text-sm font-bold text-slate-800" style={{ margin: 0 }}>
                  Titre de la notification <span className="text-rose-500">*</span>
                </label>
                <span className={'text-sm font-mono font-semibold ' + (translations[activeLang].title.length > 40 ? 'text-rose-600' : 'text-slate-400')}>
                  {translations[activeLang].title.length} / 40 caractères
                </span>
              </div>
              <input
                type="text"
                placeholder={activeLang === 'fr' ? 'ex: 🥐 Commandez vos viennoiseries avant 19h !' : 'Tapez la traduction ici...'}
                value={translations[activeLang].title}
                onChange={(e) => handleTextChange('title', e.target.value)}
                className={
                  'browser-default w-full text-sm rounded-xl border p-2 focus:ring-2 focus:outline-none transition-all ' +
                  (translations[activeLang].title.length > 40
                    ? 'border-rose-400 text-rose-900 bg-rose-50'
                    : 'border-slate-200 text-slate-800 hover:border-slate-300')
                }
              />
              {translations[activeLang].title.length > 40 && (
                <p className="text-rose-600 text-sm font-bold flex items-center gap-1">
                  <ExclamationIcon className="w-3 h-3 flex-shrink-0" /> Le titre ne doit pas faire plus de 40 caractères.
                </p>
              )}
            </div>

            {/* Champ Corps */}
            <div className="space-y-1">
              <div className="flex justify-between items-baseline">
                <label className="text-sm font-bold text-slate-800" style={{ margin: 0 }}>
                  Corps du message push <span className="text-rose-500">*</span>
                </label>
                <span className={'text-sm font-mono font-semibold ' + (translations[activeLang].body.length > 140 ? 'text-rose-600' : 'text-slate-400')}>
                  {translations[activeLang].body.length} / 140 caractères
                </span>
              </div>
              <textarea
                rows={3}
                placeholder={activeLang === 'fr' ? "ex: Récupérez vos croissants chauds dès 07h30 à l'épicerie !" : 'Tapez la traduction du corps du texte ici...'}
                value={translations[activeLang].body}
                onChange={(e) => handleTextChange('body', e.target.value)}
                className={
                  'browser-default w-full text-sm rounded-xl border p-2 focus:ring-2 focus:outline-none transition-all resize-none ' +
                  (translations[activeLang].body.length > 140
                    ? 'border-rose-400 text-rose-900 bg-rose-50'
                    : 'border-slate-200 text-slate-800 hover:border-slate-300')
                }
              />
              {translations[activeLang].body.length > 140 && (
                <p className="text-rose-600 text-sm font-bold flex items-center gap-1">
                  <ExclamationIcon className="w-3 h-3 flex-shrink-0" /> Le message ne doit pas dépasser 140 caractères.
                </p>
              )}
            </div>

            {!activeLangValidation.valid && (
              <div className="p-2 bg-rose-50 border border-rose-100 rounded-xl text-sm text-rose-700 font-mono flex items-center gap-1">
                <ExclamationIcon className="w-4 h-4 text-rose-500 flex-shrink-0" />
                <span>{activeLangValidation.error}</span>
              </div>
            )}
          </div>

          {/* Section Destination au clic */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-3">
            <div className="flex items-center gap-2 border-b border-gray-100 pb-2">
              <AdjustmentsIcon className="w-4 h-4 text-slate-500" />
              <h3 className="font-bold text-slate-800 text-sm my-0">Destination au clic</h3>
            </div>
            <p className="text-sm text-slate-400">
              Page de l'application campeur vers laquelle rediriger au clic sur la notification :
            </p>
            <div className="relative">
              <select
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
                className="browser-default w-full text-sm rounded-xl border border-slate-200 p-2 bg-white text-slate-800 font-semibold focus:outline-none focus:ring-2 focus:ring-indigo-100 hover:border-slate-300 transition-all cursor-pointer"
              >
                {destinations.map(dest => (
                  <option key={dest.value} value={dest.value}>
                    <span role="img" aria-label="destination">🗺️</span> {dest.label}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-slate-400">
                <ChevronDownIcon className="w-4 h-4" />
              </div>
            </div>
          </div>

          {/* Section C: Audience de Destinataires */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-4">
            <div className="flex items-center justify-between border-b border-gray-100 pb-2">
              <div className="flex items-center gap-1.5">
                <BellIcon className="w-4 h-4 text-slate-500" />
                <h3 className="font-bold text-slate-800 text-sm my-0">Audience</h3>
              </div>
              <span className="text-sm bg-slate-100 text-slate-600 font-mono px-3 py-1 rounded-full font-bold uppercase tracking-wider">
                Calcul temps réel
              </span>
            </div>

            <div className="space-y-3">
              {[
                ...(clientSegmentInbound ? [{
                  id: 'custom_segment',
                  label: 'Segment de recherche de contacts',
                  count: null,
                  desc: 'Clients sélectionnés depuis votre outil CRM de recherche de contacts.'
                }] : []),
                {
                  id: 'all',
                  label: 'Tous les campeurs',
                  count: null,
                  desc: "La notification apparaît dans l'espace notifications de tous les campeurs. Sur les appareils ayant activé le push, une alerte est également émise."
                },
                {
                  id: 'on_site',
                  label: 'Sur site en ce moment',
                  count: null,
                  desc: "Cible les campeurs dont le séjour est en cours aujourd'hui, selon leurs dates d'arrivée et de départ."
                },
                {
                  id: 'arriving_today',
                  label: "Arrivent aujourd'hui",
                  count: null,
                  desc: "Idéal pour les messages de bienvenue, la remise des clés, les codes barrières ou d'autres instructions d'arrivée."
                },
                {
                  id: 'arriving_7_days',
                  label: 'Arrivent dans les 7 jours',
                  count: null,
                  desc: "Préparez leur séjour en rappelant les informations pratiques indispensables, cautions, suppléments ou réservations d'activités."
                },
                {
                  id: 'leaving_7_days',
                  label: 'Partent dans les 7 jours',
                  count: null,
                  desc: "Parfait pour recueillir des questionnaires de satisfaction, proposer des offres de réinscription ou rappeler les formalités de départ."
                },
                {
                  id: 'overlap_period',
                  label: 'Séjour qui chevauche la période',
                  count: null,
                  desc: "Ciblez précisément les clients présents durant un événement, un week-end prolongé ou des dates spécifiques."
                }
              ].map((opt) => {
                const isSelected = audienceOption === opt.id
                const counts = isSelected
                  ? { users: audienceCountUsers, push: audienceCountPush }
                  : allCounts[opt.id]
                const displayCount = (counts && counts.users !== undefined) ? counts.users : null
                return (
                  <div key={opt.id} className="space-y-2">
                    <label
                      style={{ margin: 0 }}
                      className={
                        'p-3.5 rounded-xl border flex items-start gap-3.5 transition-all text-left cursor-pointer ' +
                        (isSelected
                          ? 'bg-indigo-50 border-indigo-400 text-indigo-900 ring-1 ring-indigo-200'
                          : 'bg-white hover:bg-slate-50 border-slate-200 text-slate-700')
                      }
                    >
                      <input
                        type="radio"
                        name="audienceOption"
                        value={opt.id}
                        checked={isSelected}
                        onChange={() => setAudienceOption(opt.id)}
                        className="browser-default cursor-pointer"
                        style={{ position: 'static', opacity: 1, pointerEvents: 'auto', width: '14px', height: '14px', flexShrink: 0, marginTop: '2px' }}
                      />
                      <div className="flex-1 text-sm">
                        <span className="font-semibold block text-slate-800 text-sm">
                          {opt.label}
                          {isSelected && audienceLoading
                            ? <span className="text-indigo-600 font-mono font-bold ml-1.5">(...)</span>
                            : displayCount !== undefined && displayCount !== null
                              ? <span className={'font-mono font-bold ml-1.5 ' + (isSelected ? 'text-indigo-600' : 'text-slate-400')}>
                                  {'(' + displayCount + ' campeur' + (displayCount > 1 ? 's' : '') + ')'}
                                </span>
                              : null
                          }
                        </span>
                        <p className="text-slate-400 text-sm mt-0.5 leading-relaxed font-normal my-0">{opt.desc}</p>
                      </div>
                    </label>

                    {/* Date pickers pour "Séjour qui chevauche la période" */}
                    {opt.id === 'overlap_period' && isSelected && (
                      <div className="grid grid-cols-2 gap-4 ml-8 p-3.5 bg-slate-50 rounded-xl border border-slate-200">
                        <div>
                          <label className="text-sm uppercase font-bold text-slate-400 tracking-wider block mb-1" style={{ margin: 0 }}>Du</label>
                          <input
                            type="date"
                            value={overlapStart}
                            onChange={(e) => setOverlapStart(e.target.value)}
                            className="browser-default w-full text-sm rounded-lg border border-slate-200 p-2 text-slate-800 font-semibold bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-100"
                          />
                        </div>
                        <div>
                          <label className="text-sm uppercase font-bold text-slate-400 tracking-wider block mb-1" style={{ margin: 0 }}>Au</label>
                          <input
                            type="date"
                            value={overlapEnd}
                            onChange={(e) => setOverlapEnd(e.target.value)}
                            className="browser-default w-full text-sm rounded-lg border border-slate-200 p-2 text-slate-800 font-semibold bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-100"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

            {/* Filter by accommodation / pitch category */}
            <div className="pt-4 border-t border-slate-100 space-y-2.5">
              <span className="text-xs uppercase font-extrabold text-slate-400 tracking-wider block">
                Type d'hébergement / Emplacement
              </span>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 bg-slate-50 p-1.5 rounded-xl border border-slate-200">
                {[
                  { id: 'all', label: 'Tous' },
                  { id: 'pitch', label: 'Emplacements nus' },
                  { id: 'rental', label: 'Locatifs' },
                  { id: 'resident', label: 'Résidents' }
                ].map((item) => {
                  const isActive = accommodationFilter === item.id
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setAccommodationFilter(item.id)}
                      style={{ backgroundImage: 'none', backgroundColor: isActive ? '#4f46e5' : '#ffffff' }}
                      className={`py-2 px-2.5 rounded-lg text-sm font-bold transition-all text-center select-none cursor-pointer border ${
                        isActive
                          ? 'border-indigo-700 text-white shadow-xs'
                          : 'hover:bg-slate-50 border-slate-200 text-slate-600'
                      }`}
                    >
                      {item.label}
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Récapitulatif audience */}
            <div className="bg-slate-50 rounded-xl p-3.5 flex justify-between items-center text-sm text-slate-600 border border-slate-200 mt-2">
              <div className="flex items-center gap-2">
                <CheckCircleIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                <span className="text-slate-700 font-medium text-sm tracking-wide">Cible sélectionnée :</span>
                <span className="font-mono bg-white font-bold border border-slate-200 px-2.5 py-0.5 rounded-md text-indigo-600 text-sm shadow-sm">
                  {audienceLoading
                    ? <span className="text-slate-400">...</span>
                    : audienceCountUsers !== null
                      ? audienceCountUsers + ' campeur(s) · dont ' + audienceCountPush + ' avec push'
                      : audienceLabel
                  }
                </span>
              </div>
            </div>
          </div>

          {/* Section D: Planification d'envoi */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-4">
            <div className="flex items-center gap-1.5 border-b border-gray-100 pb-2">
              <CalendarIcon className="w-4 h-4 text-slate-500" />
              <h3 className="font-bold text-slate-800 text-sm my-0">Mode de diffusion</h3>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <button
                id="send-now-radio-btn"
                type="button"
                onClick={() => setSendType('now')}
                className={
                  'p-3 rounded-xl border flex flex-col items-center gap-1.5 transition-all cursor-pointer ' +
                  (sendType === 'now'
                    ? 'bg-indigo-50 border-indigo-400 text-indigo-900 ring-1 ring-indigo-200'
                    : 'bg-white hover:bg-slate-50 border-slate-200 text-slate-700')
                }
                style={{ backgroundImage: 'none', backgroundColor: sendType === 'now' ? '#eef2ff' : 'white' }}
              >
                <span className="font-bold text-sm">Envoyer maintenant <span role="img" aria-label="éclair">⚡</span></span>
                <span className="text-sm text-slate-400 text-center">Déclenchement immédiat</span>
              </button>

              <button
                id="send-schedule-radio-btn"
                type="button"
                onClick={() => setSendType('scheduled')}
                className={
                  'p-3 rounded-xl border flex flex-col items-center gap-1.5 transition-all cursor-pointer ' +
                  (sendType === 'scheduled'
                    ? 'bg-indigo-50 border-indigo-400 text-indigo-900 ring-1 ring-indigo-200'
                    : 'bg-white hover:bg-slate-50 border-slate-200 text-slate-700')
                }
                style={{ backgroundImage: 'none', backgroundColor: sendType === 'scheduled' ? '#eef2ff' : 'white' }}
              >
                <span className="font-bold text-sm">Programmer <span role="img" aria-label="horloge">⏰</span></span>
                <span className="text-sm text-slate-400 text-center">Définir date et heure</span>
              </button>
            </div>

            {sendType === 'scheduled' && (
              <div className="space-y-3 bg-slate-50 p-4 rounded-xl border border-slate-200">
                <div className="flex justify-between items-baseline gap-1">
                  <label className="text-sm font-bold text-slate-700" style={{ margin: 0 }}>Consignez la date & heure d'envoi</label>
                  <span className="text-sm text-indigo-700 font-mono font-semibold"><span role="img" aria-label="horloge">🕒</span> Europe/Paris (UTC+02)</span>
                </div>
                <input
                  id="form-scheduled-date-input"
                  type="datetime-local"
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                  className={
                    'browser-default w-full text-sm rounded-xl border p-2 bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-100 ' +
                    (isDateInvalid() ? 'border-rose-400 bg-rose-50 text-rose-900' : 'border-slate-200')
                  }
                />
                {isDateInvalid() ? (
                  <div id="date-past-error" className="p-2 bg-rose-50 text-rose-800 text-sm rounded border border-rose-200 flex items-start gap-1">
                    <ExclamationIcon className="w-3.5 h-3.5 text-rose-600 flex-shrink-0" />
                    <span><strong>Erreur chronologique :</strong> La date choisie est déjà passée ({scheduledDate.replace('T', ' ')}). Veuillez sélectionner un moment futur.</span>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 italic my-0">
                    La notification sera mise en file d'attente et émise automatiquement à l'heure renseignée. Modifiable ou annulable à tout moment.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Boutons d'action */}
          <div className="flex items-center gap-3 pt-2">
            <button
              id="form-cancel-btn"
              type="button"
              onClick={onCancel}
              className="px-5 py-2.5 rounded-lg border border-slate-200 text-slate-600 hover:text-slate-800 hover:bg-slate-50 font-semibold text-sm transition-colors flex-shrink-0 cursor-pointer"
              style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
            >
              Annuler
            </button>
            <button
              id="form-submit-preview-btn"
              type="button"
              onClick={handlePreSubmit}
              disabled={!submittable}
              className={
                'flex-1 font-semibold text-sm px-5 py-2.5 rounded-lg transition-all text-center flex items-center justify-center gap-2 border shadow-sm ' +
                (submittable
                  ? 'bg-indigo-600 hover:bg-indigo-700 text-white cursor-pointer border-indigo-700'
                  : 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed')
              }
              style={{ backgroundImage: 'none', backgroundColor: submittable ? '#4f46e5' : '#f1f5f9' }}
            >
              <CheckIcon className="w-4 h-4" />
              Consulter le résumé & Envoyer
            </button>
          </div>

            </>
          )}

        </div>

        {/* Colonne aperçu mobile */}
        <div className="lg:col-span-2 flex flex-col">
          <div className="sticky top-6 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-mono font-bold text-slate-400 uppercase tracking-widest">Prévisualisation mobile</span>
              <span className="text-sm bg-emerald-100 text-emerald-800 px-2 rounded-full font-bold">Temps réel</span>
            </div>

            {/* Contour style iPhone */}
            <div
              className="border-8 border-slate-900 rounded-3xl bg-slate-950 p-2 shadow-2xl relative w-full max-w-xs mx-auto overflow-hidden"
              style={{ aspectRatio: '9/18' }}
            >
              <div className="absolute top-3 left-6 right-6 flex justify-between z-10">
                <span className="text-sm font-mono" style={{ color: 'rgba(255,255,255,0.4)' }}>10:09</span>
                <span className="text-sm font-mono" style={{ color: 'rgba(255,255,255,0.4)' }}>5G</span>
              </div>

              <div
                className="rounded-2xl overflow-hidden w-full h-full relative p-4 flex flex-col justify-start pt-12"
                style={{ background: 'linear-gradient(to bottom, #134e4a, #064e3b, #0f172a)' }}
              >
                {/* Bulle de notification */}
                <div className="bg-white rounded-2xl p-3 shadow-xl border border-slate-100 text-slate-900 mt-2">
                  <div className="flex justify-between items-center mb-1">
                    <div className="flex items-center gap-1">
                      <div className="w-5 h-5 rounded bg-indigo-600 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm text-white font-bold" role="img" aria-label="tente">⛺</span>
                      </div>
                      <span className="font-black text-sm text-slate-900 tracking-tight uppercase">PocketCamp</span>
                    </div>
                    <span className="text-sm text-slate-400 font-mono">À l'instant</span>
                  </div>
                  <h4 className="font-bold text-sm text-slate-900 tracking-tight block truncate">
                    {previewTitle}
                  </h4>
                  <p className="text-sm text-slate-500 leading-snug mt-1 break-words line-clamp-3">
                    {previewBody}
                  </p>
                </div>

                <div
                  className="absolute inset-x-0 bottom-4 text-center text-sm font-mono"
                  style={{ color: 'rgba(255,255,255,0.25)' }}
                >
                  [ Écran de verrouillage ]
                </div>
              </div>
            </div>

            <div className="bg-slate-50 rounded-2xl p-4 border border-slate-200 text-sm text-slate-500 leading-normal space-y-1">
              <span className="font-bold text-slate-700 block"><span role="img" aria-label="ampoule">💡</span> Remarque :</span>
              <p>
                L'aperçu s'adapte automatiquement à l'onglet linguistique en cours d'édition pour évaluer la longueur de vos traductions.
              </p>
            </div>
          </div>
        </div>

      </div>
      )}

    </div>
  )
}

// --- Composant principal ---

function NotificationHub(props) {
  const {
    notifications,
    cron_notifications,
    lang_default,
    user,
    fetchNotifications,
    fetchCronNotifications,
    updateNavbarTitle
  } = props

  const userRef = useRef(user)
  useEffect(() => { userRef.current = user }, [user])

  const [view, setView] = useState('hub')
  const [formOrigin, setFormOrigin] = useState('hub')
  const [editingTemplate, setEditingTemplate] = useState(null)
  const [initialNotificationForForm, setInitialNotificationForForm] = useState(null)
  const [formSendType, setFormSendType] = useState('now')
  const [clientSegmentInbound, setClientSegmentInbound] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [selectedNotification, setSelectedNotification] = useState(null)
  const [historyLimit, setHistoryLimit] = useState(10)
  const [templateSaving, setTemplateSaving] = useState(false)
  const [templateToDelete, setTemplateToDelete] = useState(null)
  const [deletingTemplate, setDeletingTemplate] = useState(false)
  const [libraryOrigin, setLibraryOrigin] = useState('hub')
  const [localTemplates, setLocalTemplates] = useState(null)
  const [localPendingScheduled, setLocalPendingScheduled] = useState([])
  const [notificationToCancel, setNotificationToCancel] = useState(null)

  useEffect(() => {
    if (updateNavbarTitle)
      updateNavbarTitle('Notifications push')
    if (fetchNotifications)
      fetchNotifications()
    if (fetchCronNotifications)
      fetchCronNotifications()
  }, [])

  useEffect(() => {
    setHistoryLimit(10)
  }, [searchQuery, statusFilter])

  if (cron_notifications === null || cron_notifications === undefined) {
    return (
      <div className="loader">
        <Loader />
      </div>
    )
  }

  const now = Math.round(Date.now() / 1000)
  const allCronNotifications = [...(cron_notifications || []), ...localPendingScheduled]
  const allTemplates = (localTemplates !== null ? localTemplates : (notifications || [])).slice().sort((a, b) => {
    const ts = id => {
      const s = String(id || '')
      if (s.startsWith('local_')) return parseInt(s.replace('local_', ''), 10)
      if (s.length === 24) return parseInt(s.substring(0, 8), 16) * 1000
      return 0
    }
    return ts(b._id) - ts(a._id)
  })

  const scheduledNotifications = allCronNotifications.filter(cn => getCronStatus(cn, now) === 'scheduled').sort((a, b) => b.time - a.time)
  const historyNotifications = allCronNotifications.filter(cn => getCronStatus(cn, now) !== 'scheduled').sort((a, b) => b.time - a.time)

  const currentMonth = new Date().getMonth()
  const currentYear = new Date().getFullYear()
  const sentThisMonth = allCronNotifications.filter(cn => {
    if (!cn.sent || cn.sent === false)
      return false
    const ref = typeof cn.sent === 'number' ? cn.sent : cn.time
    const d = new Date(ref * 1000)
    return d.getMonth() === currentMonth && d.getFullYear() === currentYear
  }).length

  const failedCount = allCronNotifications.filter(cn => getCronStatus(cn, now) === 'failed').length

  const filteredHistory = historyNotifications.filter(cn => {
    const title = getTitle(cn.translations, lang_default)
    const matchesSearch = title.toLowerCase().includes(searchQuery.toLowerCase())
    if (!matchesSearch)
      return false
    const status = getCronStatus(cn, now)
    if (statusFilter === 'sent')
      return status === 'sent'
    if (statusFilter === 'failed')
      return status === 'failed'
    return true
  })

  const visibleHistory = filteredHistory.slice(0, historyLimit)

  function navigateToForm(params) {
    setInitialNotificationForForm(null)
    setFormSendType((params && params.sendType) || 'now')
    setClientSegmentInbound(false)
    setFormOrigin('hub')
    setView('form')
  }

  function handleDuplicate(cn) {
    const languages = buildLanguagesFromTranslations(cn.translations)
    setInitialNotificationForForm({
      languages,
      redirect: cn.redirect ? JSON.stringify(cn.redirect) : '{"type":"home"}',
      audienceOption: 'all',
      sendType: 'now'
    })
    setFormSendType('now')
    setSelectedNotification(null)
    setView('form')
  }

  function handleCancelScheduled(cn) {
    setNotificationToCancel(cn)
  }

  function handleConfirmCancelScheduled() {
    if (!notificationToCancel)
      return
    const id = notificationToCancel._id
    setNotificationToCancel(null)
    setSelectedNotification(null)
    if (String(id).startsWith('local_'))
    {
      setLocalPendingScheduled(prev => prev.filter(item => item._id !== id))
      return ;
    }
    const userId = userRef.current && userRef.current._id ? String(userRef.current._id) : null
    fetch(connexion.connect.url + 'dashboard/cron_notification/cancel', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: String(id), user: userId })
    })
    .then(r => r.json())
    .then(ret => {
      if (ret !== false && fetchCronNotifications)
        fetchCronNotifications()
    })
    .catch(() => {})
  }

  function handleDelete(id) {
    setSelectedNotification(null)
    if (String(id).startsWith('local_'))
    {
      setLocalPendingScheduled(prev => prev.filter(item => item._id !== id))
      return ;
    }
    const userId = userRef.current && userRef.current._id ? String(userRef.current._id) : null
    fetch(connexion.connect.url + 'dashboard/cron_notification/delete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: String(id), user: userId })
    })
    .then(r => r.json())
    .then(ret => {
      if (ret !== false && fetchCronNotifications)
        fetchCronNotifications()
    })
    .catch(() => {})
  }

  function handleDeleteTemplateConfirm() {
    if (!templateToDelete || deletingTemplate)
      return
    setDeletingTemplate(true)
    const id = templateToDelete._id
    const userId = userRef.current && userRef.current._id ? String(userRef.current._id) : null
    fetch(connexion.connect.url + 'dashboard/notifications/delete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, user: userId })
    })
    .then(r => r.json())
    .then(() => {
      const base = localTemplates !== null ? localTemplates : (notifications || [])
      setLocalTemplates(base.filter(t => String(t._id) !== String(id)))
      setDeletingTemplate(false)
      setTemplateToDelete(null)
      setView('library')
      if (fetchNotifications)
        fetchNotifications().then(() => setLocalTemplates(null)).catch(() => {})
    })
    .catch(() => {
      setDeletingTemplate(false)
    })
  }

  function renderDeleteTemplateModal() {
    if (!templateToDelete)
      return null
    return (
      <div
        className="fixed inset-0 bg-slate-900 bg-opacity-60 flex items-center justify-center p-4 animate-fade-in"
        style={{ zIndex: 55 }}
        id="confirm-delete-template-overlay"
      >
        <div
          className="bg-white rounded-2xl shadow-2xl border border-slate-200 max-w-md w-full overflow-hidden text-slate-800 animate-fade-in"
          id="confirm-delete-template-box"
        >
          <div className="bg-rose-50 p-5 border-b border-rose-100 flex items-center gap-3.5">
            <div className="w-10 h-10 bg-rose-100 border border-rose-200 rounded-full flex items-center justify-center flex-shrink-0">
              <TrashIcon className="w-5 h-5 text-rose-600" />
            </div>
            <div>
              <h3 className="font-bold text-slate-900 text-sm" style={{ margin: 0 }}>Supprimer le modèle</h3>
              <p className="font-mono text-slate-400 tracking-tight" style={{ fontSize: '10px', margin: 0 }}>
                IDENTIFIANT : #{templateToDelete._id ? String(templateToDelete._id).slice(-8).toUpperCase() : '—'}
              </p>
            </div>
          </div>
          <div className="p-6 space-y-4">
            <p className="text-sm text-slate-600 leading-relaxed">
              Êtes-vous sûr de vouloir <strong>supprimer définitivement</strong> ce modèle ?
            </p>
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 text-sm space-y-3">
              <div className="space-y-1">
                <span className="font-mono font-bold text-slate-400 uppercase tracking-widest block text-sm">NOM DU MODÈLE :</span>
                <p className="font-bold text-sm text-slate-800 leading-tight" style={{ margin: 0 }}>{templateToDelete.name || '(Sans nom)'}</p>
              </div>
              <div className="space-y-1 pt-3 border-t border-slate-200">
                <span className="font-mono font-bold text-slate-400 uppercase tracking-widest block text-sm">TITRE DU MESSAGE :</span>
                <p className="text-slate-600 text-sm leading-relaxed line-clamp-3" style={{ margin: 0 }}>
                  {getTitle(templateToDelete.translations, lang_default)}
                </p>
              </div>
            </div>
          </div>
          <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-2.5">
            <button
              type="button"
              id="cancel-delete-template-btn"
              onClick={() => setTemplateToDelete(null)}
              disabled={deletingTemplate}
              className="px-4 py-2 bg-white hover:bg-slate-100 border border-slate-200 text-sm font-semibold text-slate-700 rounded-xl transition-all cursor-pointer shadow-sm select-none disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundImage: 'none' }}
            >
              Conserver le modèle
            </button>
            <button
              type="button"
              id="confirm-delete-template-btn"
              onClick={handleDeleteTemplateConfirm}
              disabled={deletingTemplate}
              className="px-4 py-2 bg-rose-600 hover:bg-rose-700 border border-rose-700 text-sm font-bold text-white rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-sm select-none disabled:opacity-75 disabled:cursor-not-allowed"
              style={{ backgroundImage: 'none', backgroundColor: '#e11d48' }}
            >
              {deletingTemplate ? (
                <>
                  <svg className="animate-spin h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Suppression...
                </>
              ) : (
                <>
                  <TrashIcon className="w-3.5 h-3.5" />
                  Supprimer de la bibliothèque
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    )
  }

  function getStatusBadge(status) {
    switch (status) {
      case 'scheduled':
        return <span className="inline-flex items-center px-2 py-0 rounded-full text-sm font-semibold bg-amber-50 text-amber-700 border border-amber-200">Programmée</span>
      case 'sent':
        return <span className="inline-flex items-center px-2 py-0 rounded-full text-sm font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">Envoyée</span>
      case 'failed':
        return <span className="inline-flex items-center px-2 py-0 rounded-full text-sm font-semibold bg-rose-50 text-rose-700 border border-rose-200">Manquée</span>
      default:
        return <span className="inline-flex items-center px-2 py-0 rounded-full text-sm font-semibold bg-slate-100 text-slate-600 border border-slate-200">Brouillon</span>
    }
  }

  if (view === 'template-form') {
    return (
      <>
      <TemplateForm
        initialTemplate={editingTemplate}
        lang_default={lang_default}
        saving={templateSaving}
        onCancel={() => setView('library')}
        onDelete={editingTemplate ? () => setTemplateToDelete(editingTemplate) : undefined}
        onSubmit={(data) => {
          const isEdit = !!(editingTemplate && editingTemplate._id)
          if (!isEdit)
          {
            const base = localTemplates !== null ? localTemplates : (notifications || [])
            setLocalTemplates([...base, {
              _id: 'local_' + Date.now(),
              name: data.name,
              translations: data.translations,
              redirect: data.redirect,
              create_time: Math.round(Date.now() / 1000),
              usage_count: 0
            }])
          }
          setTemplateSaving(false)
          setView('library')
          const url = isEdit
            ? connexion.connect.url + 'dashboard/notifications/update'
            : connexion.connect.url + 'dashboard/notifications/insert'
          const userId = userRef.current && userRef.current._id ? String(userRef.current._id) : null
          const body = isEdit
            ? { id: editingTemplate._id, ...data, user: userId }
            : { ...data, user: userId }
          fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
          })
          .then(r => r.json())
          .then(() => {
            if (fetchNotifications)
              fetchNotifications()
              .then(() => setLocalTemplates(null))
              .catch(() => {})
          })
          .catch(() => {})
        }}
      />
      {renderDeleteTemplateModal()}
    </>
    )
  }

  if (view === 'library') {
    return (
      <>
        <TemplatesLibrary
          templates={allTemplates}
          emptyStateActive={allTemplates.length === 0}
          onSelectTemplate={(tpl) => {
            setInitialNotificationForForm({
              languages: buildLanguagesFromTranslations(tpl.translations),
              redirect: tpl.redirect ? JSON.stringify(tpl.redirect) : '{"type":"home"}',
              audienceOption: 'all',
              sendType: 'now'
            })
            setFormSendType('now')
            setFormOrigin('library')
            setView('form')
          }}
          onEditTemplate={(tpl) => {
            setEditingTemplate(tpl)
            setView('template-form')
          }}
          onDuplicateTemplate={(tpl) => {
            setInitialNotificationForForm({
              languages: buildLanguagesFromTranslations(tpl.translations),
              redirect: tpl.redirect ? JSON.stringify(tpl.redirect) : '{"type":"home"}',
              audienceOption: 'all',
              sendType: 'now'
            })
            setFormSendType('now')
            setFormOrigin('library')
            setView('form')
          }}
          onDeleteTemplate={setTemplateToDelete}
          onNewTemplate={() => {
            setEditingTemplate(null)
            setView('template-form')
          }}
          onBack={() => setView(libraryOrigin)}
        />
        {renderDeleteTemplateModal()}
      </>
    )
  }

  if (view === 'form') {
    return (
      <NotificationFormView
        initialNotification={initialNotificationForForm}
        defaultSendType={formSendType}
        clientSegmentInbound={clientSegmentInbound}
        lang_default={lang_default}
        onCancel={() => setView(formOrigin)}
        onOpenLibrary={() => { setLibraryOrigin('form'); setView('library') }}
        onSubmit={(data) => {
          const translations = []
          const langs = Object.keys(data.languages)
          for (let i = 0; i < langs.length; i++) {
            const l = data.languages[langs[i]]
            if (l.filled && l.title.trim() && l.body.trim())
              translations.push({ lang: langs[i], title: l.title, content: l.body })
          }
          if (data.sendType === 'now') {
            fetch(connexion.connect.url + 'webapp/notifications/send', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                user_list: data.audienceUserIds,
                translations,
                redirect: data.redirect
              })
            })
            .then(r => r.json())
            .then(ret => {
              if (ret !== false && fetchCronNotifications)
                fetchCronNotifications()
            })
            .catch(() => {})
          }
          else {
            const localId = 'local_' + Date.now()
            const localItem = {
              _id: localId,
              translations,
              redirect: data.redirect ? JSON.parse(data.redirect) : { type: 'home' },
              user_list: data.audienceUserIds,
              time: Math.round(new Date(data.scheduledDate).getTime() / 1000),
              sent: false
            }
            setLocalPendingScheduled(prev => [...prev, localItem])
            setView('hub')
            fetch(connexion.connect.url + 'dashboard/cron_notification/insert', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                timestamp: Math.round(new Date(data.scheduledDate).getTime() / 1000),
                user_list: data.audienceUserIds,
                translations,
                redirect: data.redirect,
                user: userRef.current && userRef.current._id ? String(userRef.current._id) : null
              })
            })
            .then(r => r.json())
            .then(ret => {
              if (ret !== false && fetchCronNotifications)
                return fetchCronNotifications().then(() => {
                  setLocalPendingScheduled(prev => prev.filter(item => item._id !== localId))
                })
            })
            .catch(() => {})
          }
        }}
      />
    )
  }

  return (
    <div className="w-full text-slate-900">

      {/* 1. Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-slate-200 pb-6 mb-8">
        <div>
          <span className="text-sm font-mono font-semibold text-indigo-600 uppercase tracking-widest block mb-1">
            Hub de notifications
          </span>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight my-0">
            Notifications push
          </h1>
          <p className="text-slate-500 text-sm mt-1 max-w-2xl">
            Gérez les messages instantanés et les alertes de séjour envoyés directement sur l'application mobile de vos campeurs.
          </p>
        </div>
      </div>

      {/* 2. KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">

        <div className="bg-white p-4 rounded-xl border border-slate-200 flex items-center space-x-4 shadow-sm">
          <div className="p-3 bg-amber-50 text-amber-600 rounded-lg flex-shrink-0">
            <CalendarIcon className="w-5 h-5" />
          </div>
          <div>
            <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Messages planifiés</p>
            <p className="text-2xl font-black text-slate-800 mt-1">{scheduledNotifications.length}</p>
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-slate-200 flex items-center space-x-4 shadow-sm">
          <div className="p-3 bg-green-50 text-green-600 rounded-lg flex-shrink-0">
            <CheckCircleIcon className="w-5 h-5" />
          </div>
          <div>
            <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Envoyés ce mois</p>
            <p className="text-2xl font-black text-slate-800 mt-1">{sentThisMonth}</p>
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-slate-200 flex items-center space-x-4 shadow-sm">
          <div className="p-3 bg-red-50 text-red-600 rounded-lg flex-shrink-0">
            <XCircleIcon className="w-5 h-5" />
          </div>
          <div>
            <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Envois manqués</p>
            <p className="text-2xl font-black text-slate-800 mt-1">{failedCount}</p>
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-slate-200 flex items-center space-x-4 shadow-sm">
          <div className="p-3 bg-blue-50 text-blue-600 rounded-lg flex-shrink-0">
            <DocumentTextIcon className="w-5 h-5" />
          </div>
          <div>
            <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Modèles enregistrés</p>
            <p className="text-2xl font-black text-slate-800 mt-1">{allTemplates.length}</p>
          </div>
        </div>
      </div>

      {/* 3. Raccourcis */}
      <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 mb-8">
        <h3 className="font-bold text-slate-800 text-sm my-0 mb-4">
          Raccourcis d'envoi rapide
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

          <button
            onClick={() => navigateToForm({ sendType: 'now' })}
            className="bg-white hover:bg-slate-100 p-4 rounded-xl border border-slate-200 transition-colors text-left flex items-center gap-3 cursor-pointer shadow-sm"
            style={{ backgroundImage: 'none', backgroundColor: 'white' }}
          >
            <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center flex-shrink-0">
              <BellIcon className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <span className="font-bold text-sm text-slate-900 block">Diffuser immédiatement</span>
              <span className="text-sm text-slate-400 block truncate mt-0">Rédiger et émettre un push en direct</span>
            </div>
            <ArrowRightIcon className="w-4 h-4 text-slate-400 flex-shrink-0" />
          </button>

          <button
            onClick={() => navigateToForm({ sendType: 'scheduled' })}
            className="bg-white hover:bg-slate-100 p-4 rounded-xl border border-slate-200 transition-colors text-left flex items-center gap-3 cursor-pointer shadow-sm"
            style={{ backgroundImage: 'none', backgroundColor: 'white' }}
          >
            <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center flex-shrink-0">
              <ClockIcon className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <span className="font-bold text-sm text-slate-900 block">Planifier une alerte</span>
              <span className="text-sm text-slate-400 block truncate mt-0">Régler précisément une date d'envoi</span>
            </div>
            <ArrowRightIcon className="w-4 h-4 text-slate-400 flex-shrink-0" />
          </button>

          <button
            onClick={() => { setLibraryOrigin('hub'); setView('library') }}
            className="bg-white hover:bg-slate-100 p-4 rounded-xl border border-slate-200 transition-colors text-left flex items-center gap-3 cursor-pointer shadow-sm"
            style={{ backgroundImage: 'none', backgroundColor: 'white' }}
          >
            <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
              <DocumentTextIcon className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <span className="font-bold text-sm text-slate-900 block">Charger un modèle</span>
              <span className="text-sm text-slate-400 block truncate mt-0">Utiliser les préréglages de votre camping</span>
            </div>
            <ArrowRightIcon className="w-4 h-4 text-slate-400 flex-shrink-0" />
          </button>
        </div>
      </div>

      {/* 4. Grille principale */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

        {/* Colonne gauche */}
        <div className="lg:col-span-2 space-y-8">

          {/* 4A. Planification active */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-5 border-b border-slate-200 flex justify-between items-center bg-slate-50">
              <div className="flex items-center gap-2">
                <CalendarIcon className="w-4 h-4 text-amber-600" />
                <h2 className="font-bold text-slate-900 text-lg my-0">Push programmés</h2>
              </div>
              <span className="text-sm bg-amber-100 text-amber-900 font-semibold px-2 py-1 rounded-full">
                {scheduledNotifications.length} programmée(s)
              </span>
            </div>

            <div className="divide-y divide-slate-100 max-h-400 overflow-y-auto">
              {scheduledNotifications.length === 0 && (
                <p className="p-6 text-sm text-slate-400 text-center">Aucune notification programmée.</p>
              )}
              {scheduledNotifications.map(cn => (
                <div key={cn._id} className="p-4 flex flex-col sm:flex-row justify-between sm:items-center gap-4 hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => setSelectedNotification(cn)}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-slate-900 text-sm truncate">
                        {getTitle(cn.translations, lang_default)}
                      </span>
                      <span className="text-sm font-mono bg-indigo-50 border border-indigo-100 uppercase text-indigo-700 px-2 py-0 rounded font-bold flex-shrink-0">
                        {getDestinationLabel(cn.redirect)}
                      </span>
                    </div>
                    <p className="text-sm text-slate-500 truncate mt-1">
                      {getBody(cn.translations, lang_default)}
                    </p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 font-mono items-center text-xs text-slate-400 mt-2">
                      <span className="flex items-center gap-1">
                        <UsersIcon className="w-3 h-3" />
                        {getAudienceLabel(cn.user_list)}
                      </span>
                      <span className="flex items-center gap-1 text-amber-600 font-semibold">
                        <ClockIcon className="w-3 h-3" />
                        {formatDate(cn.time)}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      className="text-sm text-rose-600 hover:text-rose-700 font-semibold px-3 py-1 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                      style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
                      onClick={(e) => { e.stopPropagation(); handleCancelScheduled(cn) }}
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 4B. Historique */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-5 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-slate-50">
              <div className="flex items-center gap-2">
                <RefreshIcon className="w-4 h-4 text-slate-600" />
                <h2 className="font-bold text-slate-900 my-0 text-lg">Historique des envois</h2>
              </div>
              <div className="flex items-center gap-1 bg-white p-1 rounded-xl border border-slate-200 flex-shrink-0 text-sm">
                <button
                  onClick={() => setStatusFilter('all')}
                  className={
                    'px-3 py-1 rounded-lg transition-colors font-semibold ' +
                    (statusFilter === 'all' ? 'bg-slate-900 text-white' : 'text-slate-500 hover:text-slate-900')
                  }
                  style={{ backgroundImage: 'none', backgroundColor: statusFilter === 'all' ? '#0f172a' : 'transparent' }}
                >
                  Tous
                </button>
                <button
                  onClick={() => setStatusFilter('sent')}
                  className={
                    'px-3 py-1 rounded-lg transition-colors font-semibold ' +
                    (statusFilter === 'sent' ? 'bg-green-100 text-green-900' : 'text-slate-500 hover:text-slate-900')
                  }
                  style={{ backgroundImage: 'none', backgroundColor: statusFilter === 'sent' ? '#dcfce7' : 'transparent' }}
                >
                  Envoyées
                </button>
                <button
                  onClick={() => setStatusFilter('failed')}
                  className={
                    'px-3 py-1 rounded-lg transition-colors font-semibold ' +
                    (statusFilter === 'failed' ? 'bg-red-100 text-red-900' : 'text-slate-500 hover:text-slate-900')
                  }
                  style={{ backgroundImage: 'none', backgroundColor: statusFilter === 'failed' ? '#fee2e2' : 'transparent' }}
                >
                  Manquées
                </button>
              </div>
            </div>

            <div className="px-5 py-3 border-b border-slate-200 bg-white flex items-center gap-2">
              <SearchIcon className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <input
                type="text"
                placeholder="Rechercher par titre..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="browser-default w-full border border-slate-200 rounded-lg px-4 py-2 text-sm bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="text-slate-400 hover:text-slate-600 text-sm flex-shrink-0 cursor-pointer"
                  style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
                >
                  Effacer
                </button>
              )}
            </div>

            <div className="divide-y divide-slate-100 max-h-400 overflow-y-auto">
              {filteredHistory.length === 0 && (
                <p className="p-8 text-center text-slate-400 text-sm">Aucune notification trouvée.</p>
              )}
              {visibleHistory.map(cn => {
                const status = getCronStatus(cn, now)
                return (
                  <div
                    key={cn._id}
                    className="p-4 hover:bg-slate-50 transition-colors flex flex-col sm:flex-row justify-between sm:items-center gap-4 group cursor-pointer"
                    onClick={() => setSelectedNotification(cn)}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        {getStatusBadge(status)}
                        <span className="font-bold text-slate-800 text-sm truncate group-hover:text-indigo-600 transition-colors">
                          {getTitle(cn.translations, lang_default)}
                        </span>
                      </div>
                      <p className="text-sm text-slate-500 truncate">
                        {getBody(cn.translations, lang_default)}
                      </p>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 items-center text-xs font-mono text-slate-400 mt-2">
                        <span className="flex items-center gap-1">
                          <UsersIcon className="w-3 h-3" />
                          {getAudienceLabel(cn.user_list)}
                        </span>
                        <span>{status === 'sent' ? 'Envoyée le' : 'Prévue le'} : {formatDate(cn.time)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <ChevronRightIcon className="w-4 h-4 text-slate-300 ml-1" />
                    </div>
                  </div>
                )
              })}
              {filteredHistory.length > historyLimit && (
                <div className="p-4 bg-slate-50 hover:bg-slate-100 text-center border-t border-slate-100 transition-colors">
                  <button
                    onClick={() => setHistoryLimit(prev => prev + 15)}
                    className="text-sm text-indigo-600 hover:text-indigo-800 font-bold inline-flex items-center gap-1 cursor-pointer"
                    style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
                  >
                    Afficher plus ({filteredHistory.length - historyLimit} restant{filteredHistory.length - historyLimit > 1 ? 's' : ''})
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Colonne droite — Templates */}
        <div>
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">

            <div className="p-5 border-b border-slate-100 flex justify-between items-center bg-slate-50 flex-shrink-0">
              <div className="flex items-center gap-2">
                <DocumentTextIcon className="w-4 h-4 text-slate-600" />
                <h2 className="font-bold text-slate-900 text-lg my-0">Modèles de message</h2>
              </div>
              <button
                onClick={() => { setEditingTemplate(null); setView('template-form') }}
                className="text-sm text-indigo-600 hover:text-indigo-800 font-bold inline-flex items-center gap-0 cursor-pointer"
                style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
              >
                <PlusIcon className="w-3 h-3" /> Créer
              </button>
            </div>

            {allTemplates.length === 0 ? (
              <div className="p-8 text-center">
                <DocumentTextIcon className="w-10 h-10 text-slate-200 mx-auto mb-2" />
                <p className="text-slate-500 text-sm font-semibold">Aucun modèle disponible</p>
                <p className="text-slate-400 text-sm mt-1 max-w-xs mx-auto">
                  Créez des templates de notifications prêts à l'envoi pour fluidifier vos communications d'accueil, météo ou incidents.
                </p>
                <button
                  onClick={() => { setEditingTemplate(null); setView('template-form') }}
                  className="mt-3 bg-indigo-600 hover:bg-indigo-700 font-semibold text-sm text-white px-4 py-1 rounded-lg border border-indigo-700 transition-colors cursor-pointer shadow-sm mx-auto block"
                  style={{ backgroundImage: 'none', backgroundColor: '#4f46e5' }}
                >
                  Nouveau template
                </button>
              </div>
            ) : (
              <>
                <div className="px-4 pb-4 space-y-3 max-h-400 overflow-y-auto flex-1">
                  <div className="text-sm text-slate-400 font-medium sticky top-0 bg-white -mx-4 px-4 pt-4 pb-2 z-10 border-b border-slate-100 mb-2">
                    Sélectionnez un modèle pour charger instantanément les réglages pré-établis :
                  </div>

                  {allTemplates.map(tpl => (
                    <div
                      key={tpl._id}
                      className="p-3 bg-slate-50 hover:bg-indigo-50 rounded-xl border border-slate-200 transition-colors flex flex-col justify-between"
                    >
                      <div className="min-w-0">
                        <span className="font-semibold text-sm text-indigo-900 truncate block mb-1">
                          {tpl.name}
                        </span>
                        <span className="font-bold text-slate-800 text-sm block truncate">
                          {getTitle(tpl.translations, lang_default)}
                        </span>
                        <p className="text-slate-500 text-sm line-clamp-2 mt-0 leading-relaxed">
                          {getBody(tpl.translations, lang_default)}
                        </p>
                      </div>
                      <div className="mt-2 pt-2 border-t border-slate-200 flex justify-between items-center text-sm text-slate-400 font-mono">
                        <span>{formatDate(tpl.create_time)}</span>
                        <div className="flex gap-2">
                          <button
                            className="text-sm text-indigo-600 hover:text-indigo-800 font-bold flex items-center gap-0 cursor-pointer"
                            style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
                            onClick={() => {
                              setInitialNotificationForForm({
                                languages: buildLanguagesFromTranslations(tpl.translations),
                                redirect: tpl.redirect ? JSON.stringify(tpl.redirect) : '{"type":"home"}',
                                audienceOption: 'all',
                              })
                              setFormSendType('now')
                              setFormOrigin('hub')
                              setView('form')
                            }}
                          >
                            <PlayIcon className="w-3 h-3" /> Utiliser
                          </button>
                          <button
                            className="text-sm text-slate-500 hover:text-slate-800 font-bold cursor-pointer"
                            style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
                            onClick={() => {
                              setEditingTemplate(tpl)
                              setView('template-form')
                            }}
                          >
                            Modifier
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="p-4 bg-slate-50 border-t border-slate-100 text-center flex-shrink-0">
                  <button
                    onClick={() => { setLibraryOrigin('hub'); setView('library') }}
                    className="w-full text-center p-2 rounded-lg text-sm hover:bg-slate-100 border border-slate-200 text-slate-700 font-bold tracking-wide transition-colors block cursor-pointer"
                    style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
                  >
                    Voir tous les modèles ({allTemplates.length})
                  </button>
                </div>
              </>
            )}

          </div>
        </div>
      </div>

      {/* 5. Overlay de confirmation de suppression d'un modèle */}
      {renderDeleteTemplateModal()}

      {/* 6. Overlay de confirmation d'annulation d'un envoi planifié */}
      {notificationToCancel && (
        <div
          className="fixed inset-0 bg-slate-900 bg-opacity-60 flex items-center justify-center p-4 animate-fade-in"
          style={{ zIndex: 55 }}
          id="confirm-cancel-overlay"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl border border-slate-200 max-w-md w-full overflow-hidden text-slate-800 animate-fade-in"
            id="confirm-cancel-box"
          >
            {/* Bandeau supérieur d'avertissement */}
            <div className="bg-amber-50 p-5 border-b border-amber-100 flex items-center gap-3.5">
              <div className="w-10 h-10 bg-amber-100 border border-amber-200 rounded-full flex items-center justify-center flex-shrink-0">
                <ExclamationCircleIcon className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900 text-sm my-0">Confirmation d'annulation</h3>
              </div>
            </div>

            {/* Corps du message */}
            <div className="p-6 space-y-4">
              <p className="text-sm text-slate-600 leading-relaxed">
                Êtes-vous sûr de vouloir <strong>annuler l'envoi</strong> de cette notification programmée ?
              </p>

              {/* Détails de la notification sélectionnée */}
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 text-xs space-y-3.5">
                <div className="space-y-1">
                  <span className="text-xs font-mono font-bold text-slate-400 uppercase tracking-widest block">
                    TITRE DE LA NOTIFICATION :
                  </span>
                  <p className="font-bold text-slate-800 leading-tight block my-0">
                    {getTitle(notificationToCancel.translations, lang_default)}
                  </p>
                </div>
                <div className="flex justify-between items-start pt-3 border-t border-slate-200 gap-4">
                  <div>
                    <span className="text-xs font-mono font-bold text-slate-400 uppercase tracking-widest block">
                      AUDIENCE CIBLE :
                    </span>
                    <span className="font-bold text-slate-700 text-xs block mt-0.5">
                      {getAudienceLabel(notificationToCancel.user_list)}
                    </span>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <span className="text-xs font-mono font-bold text-slate-400 uppercase tracking-widest block">
                      DATE PRÉVUE D'ENVOI :
                    </span>
                    <span className="font-mono font-bold text-amber-600 text-xs block mt-0.5">
                      {formatDate(notificationToCancel.time)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Boutons d'action du pied de page */}
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-2.5">
              <button
                type="button"
                id="keep-scheduled-btn"
                onClick={() => setNotificationToCancel(null)}
                className="px-4 py-2 bg-white hover:bg-slate-100 border border-slate-200 text-sm font-semibold text-slate-700 rounded-xl transition-all cursor-pointer shadow-sm active:scale-95 select-none"
                style={{ backgroundImage: 'none' }}
              >
                Conserver la planification
              </button>
              <button
                type="button"
                id="confirm-cancel-btn"
                onClick={handleConfirmCancelScheduled}
                className="px-4 py-2 bg-rose-600 hover:bg-rose-700 border border-rose-700 text-sm font-bold text-white rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-sm active:scale-95 select-none"
                style={{ backgroundImage: 'none', backgroundColor: '#e11d48' }}
              >
                <XIcon className="w-3.5 h-3.5" />
                Annuler l'envoi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 7. Tiroir de détail */}
      {selectedNotification && (
        <NotificationDetailDrawer
          cn={selectedNotification}
          now={now}
          onClose={() => setSelectedNotification(null)}
          onDuplicate={handleDuplicate}
          onCancelScheduled={handleCancelScheduled}
          onDelete={handleDelete}
          hideActions={getCronStatus(selectedNotification, now) === 'scheduled'}
        />
      )}
    </div>
  )
}

export default NotificationHub
